import { useState } from "react";

import PipelineTemplateFormSelect from "~/components/shared/PipelineTemplateFormSelect";
import { LegacyStepsEditor } from "~/components/pipeline/LegacyStepsEditor";

type PipelineTemplate = {
  uuid?: string;
  name: string;
  description?: string | null;
  configuration: string;
};

export type Props = {
  currentConfiguration: string;
  currentPipelineTemplateUuid: string | null;
  pipelineTemplates: Array<PipelineTemplate>;
  pipelineTemplatesRequired: boolean;
  organizationUuid: string;
  errors: { [key: string]: string };
  examplesPath: string;
};

const DEFAULT_CONFIGURATION = `
# Enter your pipeline's YAML step configuration:

# This default command changes the pipeline of a running build by uploading a configuration file.
# https://buildkite.com/docs/agent/v3/cli-pipeline
# For more information on different step types, toggle open the step guides

steps:
  - label: ":pipeline:"
    command: "buildkite-agent pipeline upload"
`.trim();

/**
 * @deprecated See ~/components/pipeline/StepsEditor.tsx
 */
const LegacyStepSettingsForm = (props: Props) => {
  const hasTemplates = props.pipelineTemplates.length > 0;
  const noTemplate: PipelineTemplate = {
    name: "Do not use a pipeline template",
    configuration: props.currentConfiguration || DEFAULT_CONFIGURATION,
  };

  // Allow users to assign a custom configuration only if pipeline templates
  // are not required on an organization
  const pipelineTemplates = props.pipelineTemplatesRequired
    ? props.pipelineTemplates
    : [noTemplate, ...props.pipelineTemplates];

  const [selectedTemplate, setSelectedTemplate] = useState(
    props.pipelineTemplates.find(
      (template) => template.uuid === props.currentPipelineTemplateUuid,
    ) || noTemplate,
  );

  const isEditable = !selectedTemplate.uuid && !props.pipelineTemplatesRequired;
  const showEditor = !props.pipelineTemplatesRequired || selectedTemplate.uuid;

  function handleSelectedTemplateChange(template: PipelineTemplate) {
    setSelectedTemplate(template);
  }

  return (
    <>
      {hasTemplates && (
        <div className="mb-2">
          <PipelineTemplateFormSelect
            name="project[pipeline_template_uuid]"
            selected={selectedTemplate}
            items={pipelineTemplates}
            onChange={handleSelectedTemplateChange}
            hasError={!!props.errors["pipeline_template_uuid"]}
          />
        </div>
      )}

      {showEditor && (
        <LegacyStepsEditor
          name="project[steps][yaml]"
          readOnly={!isEditable}
          value={selectedTemplate?.configuration}
          trackingContext={{ organizationUuid: props.organizationUuid }}
          examplesPath={props.examplesPath}
        />
      )}
    </>
  );
};

export default LegacyStepSettingsForm;
